// Utility functions
const clamp = (num) => Math.min(255, Math.max(0, Math.round(num)));

const hexToRGB = (hex) => {
    // Remove # and spaces
    hex = hex.replace(/[#\s]/g, '');
    
    // Handle both 3-digit and 6-digit hex
    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }
    
    // Validate hex format
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        return null;
    }
    
    return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16)
    ];
};

const parseRGB = (color) => {
    const matches = color.match(/rgb\(?\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)?/);
    return matches ? [+matches[1], +matches[2], +matches[3]] : null;
};

const parseColor = (color) => {
    if (!color) return null;
    color = color.trim();
    return color.startsWith('#') ? hexToRGB(color) : parseRGB(color);
};

const adjustRGBValues = (rgb, percentage) => {
    const factor = 1 + (percentage / 100);
    return rgb.map(value => clamp(value * factor));
};

// Gradient parsing
const parseGradient = (input) => {
    const gradientRegex = /(linear|radial)-gradient\s*\((([^()]*|\([^()]*\))*)\)/i;
    const match = input.match(gradientRegex);
    if (!match) return null;

    const [_, type, content] = match;
    
    // Split by commas but ignore commas within rgb()
    const parts = content.split(/,(?![^(]*\))/);
    
    // Parse gradient components
    let angle = null;
    let colors = [];

    parts.forEach((part, index) => {
        part = part.trim();
        
        // Check for angle in first part
        if (index === 0 && /deg|turn|rad|grad/.test(part)) {
            angle = part;
            return;
        }

        // Extract color and stop
        const colorMatch = part.match(/((#[0-9A-Fa-f]{3,6}|rgb\s*\([^)]+\))\s*)([\d.]+%)?/);
        if (colorMatch) {
            colors.push({
                color: colorMatch[1].trim(),
                stop: colorMatch[3] ? colorMatch[3].trim() : null
            });
        }
    });

    return {
        type,
        angle,
        colors
    };
};

// Main adjustment function
export const adjustColor = (input, percentage) => {
    // Validate inputs
    if (!input || typeof input !== 'string') return null;
    if (percentage < -100 || percentage > 100) return null;

    try {
        // Handle gradient
        if (input.includes('gradient')) {
            const gradient = parseGradient(input);
            if (!gradient) return null;

            const adjustedColors = gradient.colors.map(({ color, stop }) => {
                const rgb = parseColor(color);
                if (!rgb) return null;
                
                const adjusted = adjustRGBValues(rgb, percentage);
                return `rgb(${adjusted.join(', ')})${stop ? ' ' + stop : ''}`;
            });

            if (adjustedColors.includes(null)) return null;

            const anglePart = gradient.angle ? gradient.angle + ', ' : '';
            return `${gradient.type}-gradient(${anglePart}${adjustedColors.join(', ')})`;
        }
        
        // Handle single color
        const rgb = parseColor(input);
        if (!rgb) return null;
        
        const adjusted = adjustRGBValues(rgb, percentage);
        return `rgb(${adjusted.join(', ')})`;

    } catch (error) {
        return null;
    }
};